import { Outlet, Navigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { AppSidebar } from "./AppSidebar";
import { TopBar } from "./TopBar";
import { useAuth } from "@/contexts/AuthContext";
import { setReturnPath } from "@/lib/session";
import { Skeleton } from "@/components/ui/skeleton";

function LoadingScreen() {
  return (
    <div
      className="flex h-screen w-full items-center justify-center"
      aria-label="Loading application"
    >
      <div className="space-y-3 w-64">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    </div>
  );
}

export function DashboardLayout() {
  const { user, isLoading } = useAuth();
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [desktopNavCollapsed, setDesktopNavCollapsed] = useState(
    () => localStorage.getItem("admin-sidebar-collapsed") === "true",
  );

  // Apply persisted theme preference on first render
  useEffect(() => {
    const saved = localStorage.getItem("theme");
    if (saved === "dark") {
      document.documentElement.classList.add("dark");
    } else if (saved === "light") {
      document.documentElement.classList.remove("dark");
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(
      "admin-sidebar-collapsed",
      desktopNavCollapsed ? "true" : "false",
    );
  }, [desktopNavCollapsed]);

  if (isLoading) return <LoadingScreen />;
  if (!user) {
    // Preserve the page the user tried to reach so login can return them here.
    setReturnPath(`${location.pathname}${location.search}`);
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="relative flex h-screen overflow-hidden bg-background">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-50 rounded bg-primary px-3 py-2 text-primary-foreground"
      >
        Skip to main content
      </a>
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-32 right-0 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute bottom-0 left-1/3 h-80 w-80 rounded-full bg-accent/10 blur-3xl" />
      </div>
      <AppSidebar
        mobileOpen={mobileNavOpen}
        onMobileClose={() => setMobileNavOpen(false)}
        desktopCollapsed={desktopNavCollapsed}
        onDesktopCollapsedChange={setDesktopNavCollapsed}
      />
      <div className="relative flex flex-1 flex-col overflow-hidden">
        <TopBar onMobileMenuToggle={() => setMobileNavOpen((p) => !p)} />
        <main
          id="main-content"
          className="flex-1 overflow-y-auto p-6"
          role="main"
          tabIndex={-1}
          aria-label="Main content"
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
}
